import { Userpilot } from "userpilot";
import { useAuthenticator } from "@aws-amplify/ui-react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faLockKeyhole,
  faTriangleExclamation,
  faCircleXmark,
  faCircleExclamation,
} from "@fortawesome/sharp-regular-svg-icons";

import { useState } from "react";
import { useQuery } from "@apollo/client";
import styles from "./appealsremainingquickview.module.scss";
import Loader from "@/components/shared/Loader";
import { gql } from "@/__generated__";

export const GET_PROVIDER_PURCHASE_INFORMATION = gql(`
  query ProviderPurchaseInfo {
    provider {
        providerId
        providerName
        freeTrialEndTimestamp
        createdTimestamp
      sponsoredAppeals {
        totalRemaining
      }
      purchases {
        appealQuantity
      }
    }
  }
`);

export function AppealsRemainingQuickView() {
  const { user } = useAuthenticator();
  const [purchaseMade, setPurchaseMade] = useState(false);
  const [creditsRemaining, setCreditsRemaining] = useState(0);
  const { loading: queryLoading, error: queryError } = useQuery(
    GET_PROVIDER_PURCHASE_INFORMATION,
    {
      fetchPolicy: "no-cache",
      onCompleted: (data) => {
        if (!data.provider) {
          throw new Error("Provider not found.");
        }

        if (data.provider.purchases && data.provider.purchases.length > 0) {
          setPurchaseMade(true);
          setCreditsRemaining(
            data.provider.sponsoredAppeals?.totalRemaining ?? 0,
          );
        } else {
          setPurchaseMade(false);
          setCreditsRemaining(0);
        }
        Userpilot.identify(user.userId, {
          provider_name: data.provider.providerName,
          provider_id: data.provider.providerId,
          provider_free_trail_end_timestamp:
            data.provider.freeTrialEndTimestamp,
          provider_created_timestamp: data.provider.createdTimestamp,
        });
      },
    },
  );

  if (queryError) {
    return (
      <div className={styles.navbarAppealsRemaining}>
        {queryError && (
          <div>
            <FontAwesomeIcon
              icon={faCircleExclamation}
              className={styles.queryErrorIcon}
            />
            {"Error loading appeals remaining..."}
          </div>
        )}
      </div>
    );
  }

  if (queryLoading) {
    return (
      <div className={styles.navbarAppealsRemaining}>
        {queryLoading && (
          <Loader color="loader_almost_black" width="36" height="36" />
        )}
      </div>
    );
  }

  return (
    <div className={styles.navbarAppealsRemaining}>
      {!purchaseMade && (
        <>
          <div className={styles.navbarNeverPurchasedAppealsIconWrapper}>
            <FontAwesomeIcon icon={faLockKeyhole} />
          </div>
          <div className={styles.navbarNeverPurchasedAppealsTitle}>
            {"Sponsor appeals"}
          </div>
          <div className={styles.navbarNeverPurchasedAppealsBody}>
            {"Purchase packs and your patients appeal for free."}
          </div>
          <Link
            to="/sponsor-appeals"
            className={styles.navbarAppealsRemainingButton}
          >
            {"Sponsor appeals"}
          </Link>
        </>
      )}
      {purchaseMade && creditsRemaining > 3 && (
        <>
          <div className={styles.navbarAppealsRemainingCount}>
            {creditsRemaining}
          </div>
          <div className={styles.navbarAppealsRemainingBody}>
            {"appeals remaining"}
          </div>
          <Link
            to="/sponsor-appeals?displayPurchaseModal=true"
            className={styles.navbarAppealsRemainingButton}
          >
            {"Sponsor appeals"}
          </Link>
        </>
      )}
      {purchaseMade && creditsRemaining > 0 && creditsRemaining <= 3 && (
        <>
          <div className={styles.navbarAppealsRemainingCountWarning}>
            {creditsRemaining}
          </div>
          <div className={styles.navbarAppealsRemainingBodyWarning}>
            {`appeal${creditsRemaining === 1 ? "" : "s"} remaining`}
          </div>
          <div className={styles.navbarAppealsRemainingAlmostOutWarning}>
            <FontAwesomeIcon
              icon={faTriangleExclamation}
              className={styles.navbarAppealsRemainingAlmostOutWarningIcon}
            />
            {"You're almost out"}
          </div>
          <Link
            to="/sponsor-appeals?displayPurchaseModal=true"
            className={styles.navbarAppealsRemainingButton}
          >
            {"Sponsor appeals"}
          </Link>
        </>
      )}
      {purchaseMade && creditsRemaining === 0 && (
        <>
          <div className={styles.navbarAppealsRemainingNoneLeftCount}>
            {"0"}
          </div>
          <div className={styles.navbarAppealsRemainingNoneLeftBody}>
            {"appeals remaining"}
          </div>
          <div className={styles.navbarAppealsRemainingNoneLeftWarning}>
            <FontAwesomeIcon
              icon={faCircleXmark}
              className={styles.navbarAppealsRemainingNoneLeftWarningIcon}
            />
            {"Patients pay for appeals"}
          </div>
          <Link
            to="/sponsor-appeals?displayPurchaseModal=true"
            className={styles.navbarAppealsRemainingButton}
          >
            {"Sponsor appeals"}
          </Link>
        </>
      )}
    </div>
  );
}
